import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCombinedScanData,
  fetchPlanData,
  fetchOCRPayments,
  addOCRPayment,
  deleteOCRPayment,
} from "../Redux/GeminiOCRSlice/GeminiOCR";
import Loader from "../Components/Loader/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import {
  FiTrendingUp,
  FiFilter,
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiCheckCircle,
  FiXCircle,
  FiLayers,
  FiDollarSign,
  FiUsers,
  FiCalendar,
  FiActivity,
  FiArrowUpRight,
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiExternalLink,
  FiBarChart2,
  FiBriefcase,
  FiArrowRight,
  FiAlertCircle,
  FiPlusCircle,
  FiTrash2,
  FiEye,
  FiCode,
  FiCopy,
  FiCheck,
  FiCreditCard,
  FiUploadCloud,
  FiX,
  FiPieChart,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://192.168.88.117:5000";

// Helper: Check if client should be excluded (DYNAMIC SOFTLINK, DSLP, Unassigned)
const isExcludedClient = (name) => {
  if (!name) return true;
  const n = String(name).trim().toUpperCase();
  if (n === "DYNAMIC SOFTLINK PVT. LTD." || n.startsWith("DYNAMIC SOFTLINK")) return true;
  if (n === "DSLP(OCR)" || n === "DSLP (OCR)" || n.startsWith("DSLP")) return true;
  if (n.includes("UNASSIGNED") || n === "UNKNOWN" || n === "NONAME") return true;
  return false;
};

// Helper: parse dates safely
const parseScanDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.trim().split(" ");
  const dParts = parts[0]?.split("-");
  if (!dParts || dParts.length < 3) {
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? null : fallback;
  }
  const tParts = parts[1] ? parts[1].split(":") : [0, 0, 0];
  const day = parseInt(dParts[0], 10);
  const month = parseInt(dParts[1], 10) - 1;
  const year = parseInt(dParts[2], 10);
  const hour = parseInt(tParts[0] || 0, 10);
  const min = parseInt(tParts[1] || 0, 10);
  const sec = parseInt(tParts[2] || 0, 10);
  return new Date(year, month, day, hour, min, sec);
};

// Normalize Status to 'True' or 'False'
const normalizeStatus = (status) => {
  if (!status) return "False";
  const s = String(status).toLowerCase().trim();
  if (s === "true" || s === "success" || s.includes("201") || s.includes("success")) return "True";
  return "False";
};

const CHART_COLORS = {
  inward: "#2563eb",
  delivery: "#9333ea",
  success: "#10b981",
  failed: "#ef4444",
  amber: "#f59e0b",
};

const OCRAnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    CombinedScanData,
    PartyBalances,
    PlanData,
    combinedLoading,
    ocrPayments,
    ocrPaymentsLoading,
    addPaymentLoading,
  } = useSelector((state) => state.GeminiOCR || {});

  // Main View Navigation: 'party_balance' | 'logs' | 'charts' | 'invoices'
  const [mainView, setMainView] = useState("party_balance");

  // Date Filter Presets: 'all' | 'today' | 'yesterday' | '7days' | '30days' | 'custom'
  const [datePreset, setDatePreset] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Filters
  const [selectedParty, setSelectedParty] = useState("all");
  const [selectedType, setSelectedType] = useState("all"); // 'all', 'Inward', 'Delivery'
  const [selectedStatus, setSelectedStatus] = useState("all"); // 'all', 'True', 'False'
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Modals state
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showApiDocModal, setShowApiDocModal] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [copiedScramble, setCopiedScramble] = useState(false);

  // New Vendor Payment Form State
  const [paymentForm, setPaymentForm] = useState({
    VendorName: "Google Cloud - Gemini API",
    InvoiceNo: "",
    InvoiceDate: new Date().toISOString().slice(0, 10),
    BillingPeriod: "",
    Amount: "",
    Currency: "INR",
    ExchangeRate: "1",
    ApiCreditsPurchased: "",
    PaymentMethod: "Bank Transfer",
    TransactionId: "",
    Notes: "",
  });
  const [invoiceFile, setInvoiceFile] = useState(null);

  // Load all initial data
  const loadData = useCallback(() => {
    dispatch(fetchCombinedScanData());
    dispatch(fetchPlanData());
    dispatch(fetchOCRPayments());
    setLastUpdated(new Date());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Extract raw scans
  const rawScansList = useMemo(() => {
    if (Array.isArray(CombinedScanData)) return CombinedScanData;
    if (CombinedScanData && Array.isArray(CombinedScanData.data)) return CombinedScanData.data;
    return [];
  }, [CombinedScanData]);

  // Extract raw plans
  const rawPlansList = useMemo(() => {
    if (Array.isArray(PlanData)) return PlanData;
    if (PlanData && Array.isArray(PlanData.data)) return PlanData.data;
    return [];
  }, [PlanData]);

  // Strict Client Filter (DYNAMIC SOFTLINK, DSLP, Unassigned Excluded)
  const processedScans = useMemo(() => {
    return rawScansList
      .filter((item) => !isExcludedClient(item?.ClientID?.WhaClientName))
      .map((item, idx) => {
        const parsedDate = parseScanDate(item.Time);
        const isSuccess = normalizeStatus(item.Status) === "True";
        const partyName = item?.ClientID?.WhaClientName?.trim() || "";
        const scanType = item.ScanType || (item.ChallanNo ? "Inward" : "Delivery");
        const refNo = item.ChallanNo || item.LotNo || "--";

        return {
          ...item,
          uniqueKey: item._id || `scan-${idx}`,
          parsedDate,
          isSuccess,
          partyName,
          scanType,
          refNo,
          cleanStatus: isSuccess ? "Success" : "Failed",
          priceVal: typeof item.Price === "number" ? item.Price : parseFloat(item.Price) || 0,
        };
      });
  }, [rawScansList]);

  // Filtered plans list
  const filteredPlans = useMemo(() => {
    return rawPlansList.filter((p) => {
      const name = p?.ClientID?.WhaClientName || p?.WhaClientName;
      return !isExcludedClient(name);
    });
  }, [rawPlansList]);

  // Date Filtering Engine
  const dateFilteredScans = useMemo(() => {
    if (datePreset === "all") return processedScans;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const endOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);

    return processedScans.filter((item) => {
      if (!item.parsedDate) return true;
      if (datePreset === "today") return item.parsedDate >= startOfToday;
      if (datePreset === "yesterday") return item.parsedDate >= startOfYesterday && item.parsedDate <= endOfYesterday;
      if (datePreset === "7days") return item.parsedDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      if (datePreset === "30days") return item.parsedDate >= new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      if (datePreset === "custom") {
        let match = true;
        if (customStartDate) {
          const s = new Date(customStartDate);
          s.setHours(0, 0, 0, 0);
          match = match && item.parsedDate >= s;
        }
        if (customEndDate) {
          const e = new Date(customEndDate);
          e.setHours(23, 59, 59, 999);
          match = match && item.parsedDate <= e;
        }
        return match;
      }
      return true;
    });
  }, [processedScans, datePreset, customStartDate, customEndDate]);

  // Calculate Party-wise Balances
  const partyWiseAnalysis = useMemo(() => {
    const map = {};

    filteredPlans.forEach((p) => {
      const cId = p.ClientID?._id?.toString() || p.ClientID?.toString() || "unknown";
      const pName = (p.ClientID?.WhaClientName || p.WhaClientName || "").trim();
      if (!pName || isExcludedClient(pName)) return;

      if (!map[pName]) {
        map[pName] = {
          partyName: pName,
          clientId: cId,
          totalRechargeApi: 0,
          totalRechargeAmount: 0,
          balApiCount: 0,
          balAmount: 0,
          price: p.Price || 1,
          plansCount: 0,
          inwardScans: 0,
          deliveryScans: 0,
          totalScans: 0,
          successScans: 0,
          failedScans: 0,
          lastScanTime: null,
        };
      }

      map[pName].totalRechargeApi += p.TotalApiCount || 0;
      map[pName].totalRechargeAmount += p.Amount || 0;
      map[pName].balApiCount += p.BalApiCount || 0;
      map[pName].balAmount += p.BalAmount || 0;
      map[pName].plansCount += 1;
      if (p.Price) map[pName].price = p.Price;
    });

    processedScans.forEach((scan) => {
      const pName = scan.partyName;
      if (!pName || isExcludedClient(pName)) return;

      if (!map[pName]) {
        map[pName] = {
          partyName: pName,
          clientId: scan.ClientID?._id?.toString() || "unknown",
          totalRechargeApi: 0,
          totalRechargeAmount: 0,
          balApiCount: 0,
          balAmount: 0,
          price: scan.priceVal || 1,
          plansCount: 0,
          inwardScans: 0,
          deliveryScans: 0,
          totalScans: 0,
          successScans: 0,
          failedScans: 0,
          lastScanTime: null,
        };
      }

      map[pName].totalScans += 1;
      if (scan.scanType === "Inward") map[pName].inwardScans += 1;
      else map[pName].deliveryScans += 1;

      if (scan.isSuccess) map[pName].successScans += 1;
      else map[pName].failedScans += 1;

      if (!map[pName].lastScanTime && scan.Time) {
        map[pName].lastScanTime = scan.Time;
      }
    });

    if (Array.isArray(PartyBalances) && PartyBalances.length > 0) {
      PartyBalances.forEach((pb) => {
        const pName = pb.partyName;
        if (pName && !isExcludedClient(pName) && map[pName]) {
          if (pb.totalApiCount) map[pName].totalRechargeApi = pb.totalApiCount;
          if (pb.totalRechargeAmount) map[pName].totalRechargeAmount = pb.totalRechargeAmount;
          if (pb.balApiCount !== undefined) map[pName].balApiCount = pb.balApiCount;
          if (pb.balAmount !== undefined) map[pName].balAmount = pb.balAmount;
        }
      });
    }

    return Object.values(map)
      .map((p) => {
        const usedApi = p.totalRechargeApi > 0 ? Math.max(0, p.totalRechargeApi - p.balApiCount) : p.totalScans;
        const usedAmount = p.totalRechargeAmount > 0 ? Math.max(0, p.totalRechargeAmount - p.balAmount) : usedApi * p.price;
        const balPct = p.totalRechargeApi > 0 ? ((p.balApiCount / p.totalRechargeApi) * 100).toFixed(1) : 0;
        const usedPct = p.totalRechargeApi > 0 ? ((usedApi / p.totalRechargeApi) * 100).toFixed(1) : 100;

        let status = "Active";
        if (p.balApiCount <= 0) {
          status = "Expired / Zero Balance";
        } else if (parseFloat(balPct) <= 20) {
          status = "Low Balance";
        }

        return {
          ...p,
          usedApi,
          usedAmount: Number(usedAmount.toFixed(2)),
          balAmount: Number(p.balAmount.toFixed(2)),
          totalRechargeAmount: Number(p.totalRechargeAmount.toFixed(2)),
          balPercent: parseFloat(balPct),
          usedPercent: parseFloat(usedPct),
          status,
        };
      })
      .sort((a, b) => b.totalRechargeApi - a.totalRechargeApi);
  }, [filteredPlans, processedScans, PartyBalances]);

  // Overall Totals
  const partyOverviewMetrics = useMemo(() => {
    let grandTotalApi = 0;
    let grandUsedApi = 0;
    let grandBalApi = 0;
    let grandTotalAmt = 0;
    let grandUsedAmt = 0;
    let grandBalAmt = 0;

    partyWiseAnalysis.forEach((p) => {
      grandTotalApi += p.totalRechargeApi;
      grandUsedApi += p.usedApi;
      grandBalApi += p.balApiCount;
      grandTotalAmt += p.totalRechargeAmount;
      grandUsedAmt += p.usedAmount;
      grandBalAmt += p.balAmount;
    });

    const overallBalPct = grandTotalApi > 0 ? ((grandBalApi / grandTotalApi) * 100).toFixed(1) : 0;
    const overallUsedPct = grandTotalApi > 0 ? ((grandUsedApi / grandTotalApi) * 100).toFixed(1) : 0;

    return {
      partiesCount: partyWiseAnalysis.length,
      grandTotalApi,
      grandUsedApi,
      grandBalApi,
      grandTotalAmt: grandTotalAmt.toFixed(2),
      grandUsedAmt: grandUsedAmt.toFixed(2),
      grandBalAmt: grandBalAmt.toFixed(2),
      overallBalPct,
      overallUsedPct,
    };
  }, [partyWiseAnalysis]);

  // Unique verified parties
  const uniqueParties = useMemo(() => {
    return partyWiseAnalysis.map((p) => p.partyName).sort();
  }, [partyWiseAnalysis]);

  // Selected party object
  const currentSelectedPartyObj = useMemo(() => {
    if (selectedParty === "all") return null;
    return partyWiseAnalysis.find((p) => p.partyName === selectedParty) || null;
  }, [partyWiseAnalysis, selectedParty]);

  // Filtered Scans for Table & Charts
  const filteredScans = useMemo(() => {
    return dateFilteredScans.filter((item) => {
      if (selectedParty !== "all" && item.partyName !== selectedParty) return false;
      if (selectedType !== "all" && item.scanType !== selectedType) return false;
      if (selectedStatus === "True" && !item.isSuccess) return false;
      if (selectedStatus === "False" && item.isSuccess) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const pMatch = item.PartyName && item.PartyName.toLowerCase().includes(q);
        const refMatch = item.refNo && item.refNo.toLowerCase().includes(q);
        const ipMatch = item.StaticIP && item.StaticIP.toLowerCase().includes(q);
        const partyMatch = item.partyName && item.partyName.toLowerCase().includes(q);
        const msgMatch = item.Message && item.Message.toLowerCase().includes(q);
        if (!pMatch && !refMatch && !ipMatch && !partyMatch && !msgMatch) return false;
      }
      return true;
    });
  }, [dateFilteredScans, selectedParty, selectedType, selectedStatus, searchQuery]);

  // Counts for quick filter buttons
  const scanCounts = useMemo(() => {
    const base = dateFilteredScans.filter((item) => {
      if (selectedParty !== "all" && item.partyName !== selectedParty) return false;
      return true;
    });
    const inward = base.filter((i) => i.scanType === "Inward").length;
    const delivery = base.filter((i) => i.scanType === "Delivery").length;
    const success = base.filter((i) => i.isSuccess).length;
    const failed = base.filter((i) => !i.isSuccess).length;
    return { all: base.length, inward, delivery, success, failed };
  }, [dateFilteredScans, selectedParty]);

  // -------------------------------------------------------------
  // VENDOR POOL USAGE & EXPENSE ANALYTICS ENGINE
  // Calculates exact consumption from purchased invoices
  // -------------------------------------------------------------
  const vendorUsageAnalytics = useMemo(() => {
    const totalSystemScans = processedScans.length;

    // Total Client Revenue collected from plans
    let totalClientRevenue = 0;
    filteredPlans.forEach((p) => {
      totalClientRevenue += (p.Amount || 0);
    });

    let totalPurchasedCredits = 0;
    let totalVendorExpenseINR = 0;

    // Sort chronologically (oldest to newest) for FIFO usage deduction
    const sortedPayments = [...(ocrPayments || [])].sort((a, b) => {
      const dateA = new Date(a.InvoiceDate || a.createdAt || 0).getTime();
      const dateB = new Date(b.InvoiceDate || b.createdAt || 0).getTime();
      return dateA - dateB;
    });

    sortedPayments.forEach((p) => {
      totalPurchasedCredits += (p.ApiCreditsPurchased || 0);
      totalVendorExpenseINR += (p.TotalAmountINR || p.Amount || 0);
    });

    // FIFO deduction across invoices
    let scansLeftToAllocate = totalSystemScans;
    const enrichedInvoices = sortedPayments.map((inv) => {
      const purchased = inv.ApiCreditsPurchased || 0;
      let consumed = 0;
      if (scansLeftToAllocate > 0 && purchased > 0) {
        consumed = Math.min(purchased, scansLeftToAllocate);
        scansLeftToAllocate -= consumed;
      }
      const remaining = Math.max(0, purchased - consumed);
      const usagePct = purchased > 0 ? Number(((consumed / purchased) * 100).toFixed(1)) : 0;
      const unitRate = inv.CostPerScan || (purchased > 0 ? Number(((inv.TotalAmountINR || inv.Amount || 0) / purchased).toFixed(4)) : 0);

      let status = "Unused";
      if (consumed >= purchased && purchased > 0) {
        status = "Fully Exhausted";
      } else if (consumed > 0) {
        status = "Active (Utilizing)";
      }

      return {
        ...inv,
        purchasedCredits: purchased,
        consumedCredits: consumed,
        remainingCredits: remaining,
        usagePercent: usagePct,
        poolStatus: status,
        unitRate,
        consumedValueINR: Number((consumed * unitRate).toFixed(2)),
        remainingValueINR: Number((remaining * unitRate).toFixed(2)),
      };
    }).reverse(); // Display newest first in table

    const totalConsumedCredits = Math.min(totalPurchasedCredits, totalSystemScans);
    const remainingVendorCredits = Math.max(0, totalPurchasedCredits - totalSystemScans);
    const deficitCredits = totalSystemScans > totalPurchasedCredits ? totalSystemScans - totalPurchasedCredits : 0;
    const overallUtilizationPercent = totalPurchasedCredits > 0
      ? Number(((totalSystemScans / totalPurchasedCredits) * 100).toFixed(1))
      : 0;

    const avgCostPerScan = totalPurchasedCredits > 0
      ? Number((totalVendorExpenseINR / totalPurchasedCredits).toFixed(4))
      : 0;

    const netProfitINR = Number((totalClientRevenue - totalVendorExpenseINR).toFixed(2));
    const profitMarginPercent = totalVendorExpenseINR > 0
      ? Number((((totalClientRevenue - totalVendorExpenseINR) / totalVendorExpenseINR) * 100).toFixed(1))
      : 0;

    return {
      totalPurchasedCredits,
      totalSystemScans,
      totalConsumedCredits,
      remainingVendorCredits,
      deficitCredits,
      overallUtilizationPercent,
      totalVendorExpenseINR: Number(totalVendorExpenseINR.toFixed(2)),
      totalClientRevenue: Number(totalClientRevenue.toFixed(2)),
      netProfitINR,
      profitMarginPercent,
      avgCostPerScan,
      enrichedInvoices,
    };
  }, [ocrPayments, processedScans, filteredPlans]);

  // Paginated Rows
  const totalPages = Math.ceil(filteredScans.length / pageSize) || 1;
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredScans.slice(start, start + pageSize);
  }, [filteredScans, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [datePreset, selectedParty, selectedType, selectedStatus, searchQuery, pageSize]);

  // Chart Data: Party Comparison
  const partyComparisonChartData = useMemo(() => {
    return partyWiseAnalysis.map((p) => ({
      name: p.partyName.length > 14 ? p.partyName.slice(0, 12) + "..." : p.partyName,
      fullName: p.partyName,
      "Used API": p.usedApi,
      "Remaining Balance": p.balApiCount,
      "Total Quota": p.totalRechargeApi,
    }));
  }, [partyWiseAnalysis]);

  // Chart Data: 14-day timeline
  const timelineChartData = useMemo(() => {
    const map = {};
    filteredScans.forEach((item) => {
      if (!item.parsedDate) return;
      const dKey = `${String(item.parsedDate.getDate()).padStart(2, "0")}/${String(
        item.parsedDate.getMonth() + 1
      ).padStart(2, "0")}`;
      const sortVal = item.parsedDate.getTime();

      if (!map[dKey]) {
        map[dKey] = { date: dKey, Inward: 0, Delivery: 0, Total: 0, sortVal };
      }
      if (item.scanType === "Inward") map[dKey].Inward++;
      else map[dKey].Delivery++;
      map[dKey].Total++;
    });

    return Object.values(map)
      .sort((a, b) => a.sortVal - b.sortVal)
      .slice(-14);
  }, [filteredScans]);

  // Export Party Balances to Excel
  const exportPartyBalancesExcel = () => {
    if (!partyWiseAnalysis.length) return alert("No party balance data to export!");

    const rows = partyWiseAnalysis.map((p, i) => ({
      "Sr No": i + 1,
      "Party Name": p.partyName,
      "Total Quota (API)": p.totalRechargeApi,
      "Used Quota (API)": p.usedApi,
      "Remaining Balance (API)": p.balApiCount,
      "Balance Quota %": `${p.balPercent}%`,
      "Unit Price (INR)": p.price,
      "Total Recharge Amount (INR)": p.totalRechargeAmount,
      "Consumed Amount (INR)": p.usedAmount,
      "Remaining Balance (INR)": p.balAmount,
      "Total Scans": p.totalScans,
      "Inward Scans": p.inwardScans,
      "Delivery Scans": p.deliveryScans,
      "Status": p.status,
      "Last Activity": p.lastScanTime || "--",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Party_Balances");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `OCR_Party_Balances_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Export Combined Scans to Excel
  const exportCombinedScansExcel = () => {
    if (!filteredScans.length) return alert("No scan records to export!");

    const rows = filteredScans.map((r, i) => ({
      "Sr No": i + 1,
      "Scan Type": r.scanType,
      "Client / Party Name": r.partyName,
      "Textile Mill (Party)": r.PartyName || "--",
      "Challan / Lot No": r.refNo,
      "Static IP": r.StaticIP || "--",
      "Status": r.cleanStatus,
      "Date Time": r.Time || "--",
      "Unit Price (INR)": r.Price || 0,
      "Bal. API Count": r.BalApiCount || "--",
      "Bal. Amount (INR)": r.BalAmount || "--",
      "Message": r.Message || "--",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scan_Logs");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `OCR_Scan_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Export Vendor Invoices & Usage to Excel
  const exportVendorInvoicesExcel = () => {
    if (!vendorUsageAnalytics.enrichedInvoices.length) return alert("No vendor invoice records to export!");

    const rows = vendorUsageAnalytics.enrichedInvoices.map((inv, idx) => ({
      "Sr No": idx + 1,
      "Vendor Name": inv.VendorName,
      "Invoice No": inv.InvoiceNo || "--",
      "Invoice Date": inv.InvoiceDate || "--",
      "Billing Period": inv.BillingPeriod || "--",
      "Purchased Credits": inv.purchasedCredits,
      "Consumed Credits (FIFO)": inv.consumedCredits,
      "Remaining Credits": inv.remainingCredits,
      "Usage %": `${inv.usagePercent}%`,
      "Pool Status": inv.poolStatus,
      "Cost Per Scan (INR)": inv.unitRate,
      "Amount Paid": `${inv.Currency} ${inv.Amount}`,
      "Total Amount (INR)": inv.TotalAmountINR,
      "Consumed Value (INR)": inv.consumedValueINR,
      "Remaining Value (INR)": inv.remainingValueINR,
      "Payment Method": inv.PaymentMethod || "--",
      "Transaction ID": inv.TransactionId || "--",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendor_OCR_Usage");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `OCR_Vendor_Usage_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Handle OCR Payment Form Submit
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.VendorName || !paymentForm.Amount) {
      toast.error("Please provide Vendor Name and Amount!");
      return;
    }

    const formData = new FormData();
    formData.append("VendorName", paymentForm.VendorName);
    formData.append("InvoiceNo", paymentForm.InvoiceNo);
    formData.append("InvoiceDate", paymentForm.InvoiceDate);
    formData.append("BillingPeriod", paymentForm.BillingPeriod);
    formData.append("Amount", paymentForm.Amount);
    formData.append("Currency", paymentForm.Currency);
    formData.append("ExchangeRate", paymentForm.ExchangeRate);
    formData.append("ApiCreditsPurchased", paymentForm.ApiCreditsPurchased || "0");
    formData.append("PaymentMethod", paymentForm.PaymentMethod);
    formData.append("TransactionId", paymentForm.TransactionId);
    formData.append("Notes", paymentForm.Notes);

    if (invoiceFile) {
      formData.append("invoiceFile", invoiceFile);
      formData.append("InvoiceFile", invoiceFile);
    }

    try {
      await dispatch(addOCRPayment(formData)).unwrap();
      toast.success("OCR Vendor Payment & Invoice recorded successfully!");
      setShowAddPaymentModal(false);
      setPaymentForm({
        VendorName: "Google Cloud - Gemini API",
        InvoiceNo: "",
        InvoiceDate: new Date().toISOString().slice(0, 10),
        BillingPeriod: "",
        Amount: "",
        Currency: "INR",
        ExchangeRate: "1",
        ApiCreditsPurchased: "",
        PaymentMethod: "Bank Transfer",
        TransactionId: "",
        Notes: "",
      });
      setInvoiceFile(null);
      dispatch(fetchOCRPayments());
    } catch (err) {
      toast.error(`Error saving payment: ${err}`);
    }
  };

  // Handle Payment Delete
  const handleDeletePayment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment record?")) return;
    try {
      await dispatch(deleteOCRPayment({ id })).unwrap();
      toast.success("Payment record deleted!");
      dispatch(fetchOCRPayments());
    } catch (err) {
      toast.error(`Delete failed: ${err}`);
    }
  };

  // Copy helpers
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === "curl") {
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2000);
    } else {
      setCopiedScramble(true);
      setTimeout(() => setCopiedScramble(false), 2000);
    }
  };

  const sampleCurl = `curl -X GET "${API_BASE_URL}/ImgToText/ShowCombinedScanLog" \\
  -H "Accept: application/json"`;

  const sampleScrambleResponse = JSON.stringify(
    {
      status: "Success",
      message: "Combined Inward & Delivery scan logs retrieved successfully (Internal accounts excluded)",
      timestamp: "18-09-2026 16:30:00",
      totalRecords: 25659,
      activePartiesCount: 4,
      counts: {
        total: 25659,
        inward: 25659,
        delivery: 0,
        success: 25648,
        failed: 11,
      },
      partyBalances: [
        {
          partyName: "SHREE NARAYAN DYEING & PRINTING MILLS",
          clientId: "676e654be91bb4ef0c9b604c",
          totalApiCount: 22000,
          totalRechargeAmount: 22000,
          balApiCount: 16750,
          balAmount: 16750,
          usedApiCount: 5250,
          usedAmount: 5250,
          price: 1,
          balancePercent: 76.1,
          accountStatus: "Active",
        },
      ],
      data: [
        {
          _id: "68cbbe7142468f76077308da",
          ScanType: "Inward",
          ChallanNo: "4376",
          PartyName: "MAHADEV TEXTILE",
          StaticIP: "152.58.48.24",
          Status: "Success(201)",
          Time: "18-09-2026 16:15:22",
          Price: 1,
          BalApiCount: 16750,
          BalAmount: 16750,
          ClientID: {
            _id: "676e654be91bb4ef0c9b604c",
            WhaClientName: "SHREE NARAYAN DYEING & PRINTING MILLS",
          },
        },
      ],
    },
    null,
    2
  );

  if (combinedLoading && !rawScansList.length) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 lg:p-8 space-y-6 text-slate-800">
      {/* ------------------------------------------------------------- */}
      {/* 1. TOP HEADER & MAIN ACTION BUTTONS                           */}
      {/* ------------------------------------------------------------- */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          {/* Header Title */}
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-xl bg-[#122A47] text-white flex items-center justify-center shadow-md">
              <FiBriefcase size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl md:text-2xl font-black tracking-tight text-[#122A47]">
                  Gemini OCR Analytics & Logs
                </h1>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse"></span>
                  English Verified
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  <FiClock size={12} />
                  Updated: {lastUpdated.toLocaleTimeString()}
                </span>
              </div>
              <p className="text-xs md:text-sm text-slate-500 mt-1">
                Client Quota Balances, Audit Scan Logs & OCR Vendor Billing Management
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Record OCR Vendor Payment Button */}
            <button
              onClick={() => setShowAddPaymentModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-[#122A47] hover:bg-[#0c2038] active:scale-95 rounded-xl shadow-sm transition duration-150"
              title="Record how much you pay to OCR Vendor with Invoice attachment"
            >
              <FiPlusCircle size={16} className="text-emerald-400" />
              <span>+ Record OCR Payment</span>
            </button>

            {/* API Documentation & Scramble Button */}
            <button
              onClick={() => setShowApiDocModal(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 active:scale-95 rounded-xl transition duration-150"
              title="View API cURL, parameters and schema scramble"
            >
              <FiCode size={16} className="text-indigo-600" />
              <span>API Scramble</span>
            </button>

            {/* Refresh Data Button */}
            <button
              onClick={loadData}
              disabled={combinedLoading}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 active:scale-95 rounded-xl shadow-sm transition duration-150 disabled:opacity-50"
              title="Refresh combined scan logs and balances"
            >
              <FiRefreshCw size={15} className={combinedLoading ? "animate-spin text-indigo-600" : "text-slate-600"} />
              <span>Refresh</span>
            </button>

            {/* Export Excel Button */}
            <button
              onClick={
                mainView === "party_balance"
                  ? exportPartyBalancesExcel
                  : mainView === "invoices"
                  ? exportVendorInvoicesExcel
                  : exportCombinedScansExcel
              }
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:scale-95 rounded-xl shadow-sm transition duration-150"
              title="Export statement to Microsoft Excel"
            >
              <FiDownload size={15} />
              <span>Export Excel</span>
            </button>
          </div>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* VIEW NAVIGATION TABS (BUTTONS)                                */}
        {/* ------------------------------------------------------------- */}
        <div className="pt-2 border-t border-slate-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto p-1 bg-slate-100/90 rounded-xl">
            {/* Tab 1: Party Balance */}
            <button
              onClick={() => setMainView("party_balance")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition duration-150 ${
                mainView === "party_balance"
                  ? "bg-[#122A47] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              }`}
            >
              <FiBriefcase size={15} />
              <span>Party Balance Statement</span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                  mainView === "party_balance" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                }`}
              >
                {partyWiseAnalysis.length} Parties
              </span>
            </button>

            {/* Tab 2: Audit Scan Logs */}
            <button
              onClick={() => setMainView("logs")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition duration-150 ${
                mainView === "logs"
                  ? "bg-[#122A47] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              }`}
            >
              <FiLayers size={15} />
              <span>Audit Scan Logs</span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                  mainView === "logs" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                }`}
              >
                {filteredScans.length.toLocaleString()}
              </span>
            </button>

            {/* Tab 3: Visual Analytics */}
            <button
              onClick={() => setMainView("charts")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition duration-150 ${
                mainView === "charts"
                  ? "bg-[#122A47] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              }`}
            >
              <FiBarChart2 size={15} />
              <span>Visual Analytics</span>
            </button>

            {/* Tab 4: Vendor Billing & Invoices */}
            <button
              onClick={() => setMainView("invoices")}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-lg transition duration-150 ${
                mainView === "invoices"
                  ? "bg-[#122A47] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
              }`}
            >
              <FiFileText size={15} />
              <span>Vendor Billing & Invoices</span>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                  mainView === "invoices" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"
                }`}
              >
                {ocrPayments.length} Records
              </span>
            </button>
          </div>

          {/* Quick Filter Status Indicator */}
          <div className="text-xs text-slate-500 flex items-center gap-2 self-end md:self-auto">
            <span className="font-semibold text-slate-700">Filter Status:</span>
            <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 font-medium">
              Internal Clients Excluded
            </span>
          </div>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* DATE RANGE FILTER BUTTONS (PROMINENT PILL BUTTONS)            */}
        {/* ------------------------------------------------------------- */}
        <div className="pt-2 border-t border-slate-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold text-slate-600 mr-1 flex items-center gap-1">
              <FiCalendar size={14} className="text-[#122A47]" /> Date Range:
            </span>

            {[
              { id: "all", label: "All Time" },
              { id: "today", label: "Today" },
              { id: "yesterday", label: "Yesterday" },
              { id: "7days", label: "Last 7 Days" },
              { id: "30days", label: "Last 30 Days" },
              { id: "custom", label: "Custom Range" },
            ].map((btn) => {
              const isActive = datePreset === btn.id;
              return (
                <button
                  key={btn.id}
                  onClick={() => setDatePreset(btn.id)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition duration-150 ${
                    isActive
                      ? "bg-[#122A47] text-white border-[#122A47] shadow-sm ring-2 ring-indigo-200"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  {btn.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Pickers */}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2 flex-wrap text-xs bg-slate-50 p-2 rounded-xl border border-slate-200">
              <span className="text-slate-600 font-medium">From:</span>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2.5 py-1 border border-slate-300 rounded-md outline-none bg-white text-xs font-medium"
              />
              <span className="text-slate-600 font-medium">To:</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2.5 py-1 border border-slate-300 rounded-md outline-none bg-white text-xs font-medium"
              />
              {(customStartDate || customEndDate) && (
                <button
                  onClick={() => {
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }}
                  className="text-rose-600 hover:underline font-bold text-[11px]"
                >
                  Reset
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* 2. TOP 4 METRIC KPI CARDS                                      */}
      {/* ------------------------------------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Allocated Quota */}
        <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm transition hover:shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
              Total Allocated Quota
            </span>
            <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl">
              <FiLayers size={20} />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-black text-slate-900 tracking-tight">
              {partyOverviewMetrics.grandTotalApi.toLocaleString()}
            </span>
            <span className="text-xs font-bold text-indigo-700 uppercase">APIs</span>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
            <span>Total Plan Amount:</span>
            <span className="font-bold text-slate-900">₹ {partyOverviewMetrics.grandTotalAmt}</span>
          </div>
          <div className="absolute top-0 left-0 h-1.5 w-full bg-[#122A47]"></div>
        </div>

        {/* Card 2: Consumed Quota */}
        <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm transition hover:shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
              Consumed / Used Quota
            </span>
            <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl">
              <FiTrendingUp size={20} />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-black text-blue-700 tracking-tight">
              {partyOverviewMetrics.grandUsedApi.toLocaleString()}
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
              {partyOverviewMetrics.overallUsedPct}% Used
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
            <span>Consumed Revenue:</span>
            <span className="font-bold text-blue-700">₹ {partyOverviewMetrics.grandUsedAmt}</span>
          </div>
          <div className="absolute top-0 left-0 h-1.5 w-full bg-blue-600"></div>
        </div>

        {/* Card 3: Available Balance */}
        <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm transition hover:shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
              Available Balance
            </span>
            <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-xl">
              <FiCheckCircle size={20} />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-black text-emerald-700 tracking-tight">
              {partyOverviewMetrics.grandBalApi.toLocaleString()}
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
              {partyOverviewMetrics.overallBalPct}% Available
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
            <span>Remaining Value:</span>
            <span className="font-bold text-emerald-700">₹ {partyOverviewMetrics.grandBalAmt}</span>
          </div>
          <div className="absolute top-0 left-0 h-1.5 w-full bg-emerald-600"></div>
        </div>

        {/* Card 4: Active Verified Clients */}
        <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm transition hover:shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
              Verified Client Parties
            </span>
            <div className="p-2.5 bg-amber-50 text-amber-700 rounded-xl">
              <FiUsers size={20} />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-black text-slate-900 tracking-tight">
              {partyOverviewMetrics.partiesCount}
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              Active
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
            <span>Filtered Scan Volume:</span>
            <span className="font-bold text-slate-900">{filteredScans.length.toLocaleString()}</span>
          </div>
          <div className="absolute top-0 left-0 h-1.5 w-full bg-amber-500"></div>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* SELECTED PARTY SPOTLIGHT BANNER                               */}
      {/* ------------------------------------------------------------- */}
      {currentSelectedPartyObj && (
        <div className="bg-[#122A47] text-white p-5 rounded-2xl shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded text-[11px] font-bold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/30">
                Filtered Client Focus
              </span>
              <span className="text-xs text-slate-300">Rate: ₹{currentSelectedPartyObj.price}/scan</span>
            </div>
            <h2 className="text-xl font-black text-white tracking-tight">{currentSelectedPartyObj.partyName}</h2>
          </div>

          <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-xs">
            <div className="bg-white/10 px-3.5 py-2 rounded-xl border border-white/10">
              <div className="text-slate-300 font-medium">Total Quota:</div>
              <div className="font-bold text-white text-sm">
                {currentSelectedPartyObj.totalRechargeApi.toLocaleString()} APIs (₹{currentSelectedPartyObj.totalRechargeAmount})
              </div>
            </div>
            <div className="bg-blue-500/20 px-3.5 py-2 rounded-xl border border-blue-400/30">
              <div className="text-blue-200 font-medium">Used Quota:</div>
              <div className="font-bold text-blue-300 text-sm">
                {currentSelectedPartyObj.usedApi.toLocaleString()} APIs (₹{currentSelectedPartyObj.usedAmount})
              </div>
            </div>
            <div className="bg-emerald-500/20 px-3.5 py-2 rounded-xl border border-emerald-400/30">
              <div className="text-emerald-200 font-medium">Available Balance:</div>
              <div className="font-bold text-emerald-300 text-sm">
                {currentSelectedPartyObj.balApiCount.toLocaleString()} APIs (₹{currentSelectedPartyObj.balAmount})
              </div>
            </div>
            <button
              onClick={() => setSelectedParty("all")}
              className="px-3.5 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-xs font-bold transition duration-150 flex items-center gap-1.5"
            >
              <span>Clear Party Filter</span>
              <FiX size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* VIEW 1: PARTY-WISE BALANCE & CONSUMPTION STATEMENT TABLE       */}
      {/* ------------------------------------------------------------- */}
      {mainView === "party_balance" && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-[#122A47]">
                  Party Balance & Quota Statement
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Complete breakdown of quota allocation, consumed scans, and remaining active balances
                </p>
              </div>

              {/* Action Buttons for Party Table */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Party Select Dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-bold">Party:</span>
                  <select
                    value={selectedParty}
                    onChange={(e) => setSelectedParty(e.target.value)}
                    className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg outline-none bg-white font-semibold text-slate-700 focus:ring-2 focus:ring-[#122A47]"
                  >
                    <option value="all">All Verified Parties ({uniqueParties.length})</option>
                    {uniqueParties.map((p, i) => (
                      <option key={i} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add Plan / Recharge Button */}
                <button
                  onClick={() => navigate("/Ocr_Plan")}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold text-[#122A47] bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-300 transition"
                  title="Go to OCR Plan creation page"
                >
                  <FiPlusCircle size={14} />
                  <span>+ Recharge Plan</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                    <th className="py-3.5 px-4">#</th>
                    <th className="py-3.5 px-4">Client / Party Name</th>
                    <th className="py-3.5 px-4">Total Quota (API)</th>
                    <th className="py-3.5 px-4 text-blue-700">Used Quota (API)</th>
                    <th className="py-3.5 px-4 text-emerald-700">Available Balance</th>
                    <th className="py-3.5 px-4">Unit Rate</th>
                    <th className="py-3.5 px-4 w-44">Balance %</th>
                    <th className="py-3.5 px-4">Account Status</th>
                    <th className="py-3.5 px-4">Total Scans</th>
                    <th className="py-3.5 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partyWiseAnalysis
                    .filter((p) => selectedParty === "all" || p.partyName === selectedParty)
                    .map((party, index) => {
                      const isSelected = selectedParty === party.partyName;
                      return (
                        <tr
                          key={party.partyName}
                          className={`transition ${
                            isSelected ? "bg-indigo-50/70 font-medium" : "hover:bg-slate-50/70"
                          }`}
                        >
                          <td className="py-4 px-4 text-slate-400 font-bold">{index + 1}</td>

                          {/* Party Name */}
                          <td className="py-4 px-4">
                            <div className="font-bold text-slate-900 text-sm">{party.partyName}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {party.plansCount > 0 ? `${party.plansCount} Active Plan Recharges` : "Direct Usage"}
                            </div>
                          </td>

                          {/* Total Quota */}
                          <td className="py-4 px-4">
                            <div className="font-bold text-slate-800">
                              {party.totalRechargeApi.toLocaleString()}{" "}
                              <span className="text-[11px] text-slate-500 font-normal">APIs</span>
                            </div>
                            <div className="text-[11px] text-slate-500 font-medium">
                              ₹ {party.totalRechargeAmount.toLocaleString()}
                            </div>
                          </td>

                          {/* Used Quota */}
                          <td className="py-4 px-4 bg-blue-50/40">
                            <div className="font-extrabold text-blue-700">
                              {party.usedApi.toLocaleString()}{" "}
                              <span className="text-[11px] font-normal">APIs</span>
                            </div>
                            <div className="text-[11px] text-blue-600 font-semibold">
                              ₹ {party.usedAmount.toLocaleString()} ({party.usedPercent}%)
                            </div>
                          </td>

                          {/* Remaining Available Balance */}
                          <td className="py-4 px-4 bg-emerald-50/40">
                            <div className="font-extrabold text-emerald-700 text-sm">
                              {party.balApiCount.toLocaleString()}{" "}
                              <span className="text-[11px] font-normal">APIs</span>
                            </div>
                            <div className="text-[11px] text-emerald-600 font-bold">
                              ₹ {party.balAmount.toLocaleString()} ({party.balPercent}%)
                            </div>
                          </td>

                          {/* Rate */}
                          <td className="py-4 px-4 text-slate-700 font-semibold">₹ {party.price}</td>

                          {/* Balance Progress Bar */}
                          <td className="py-4 px-4">
                            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden flex">
                              <div
                                className={`h-full transition-all ${
                                  party.balPercent > 20
                                    ? "bg-emerald-500"
                                    : party.balPercent > 0
                                    ? "bg-amber-500"
                                    : "bg-rose-500"
                                }`}
                                style={{ width: `${Math.min(100, party.balPercent)}%` }}
                              ></div>
                            </div>
                            <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 font-semibold">
                              <span className="text-emerald-700">{party.balPercent}% Available</span>
                              <span>{party.usedPercent}% Used</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td className="py-4 px-4">
                            {party.balApiCount > 0 ? (
                              party.balPercent <= 20 ? (
                                <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full font-bold text-[11px]">
                                  <FiAlertCircle size={12} /> Low Balance
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full font-bold text-[11px]">
                                  <FiCheckCircle size={12} /> Active
                                </span>
                              )
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-full font-bold text-[11px]">
                                <FiXCircle size={12} /> Expired
                              </span>
                            )}
                          </td>

                          {/* Completed Scans */}
                          <td className="py-4 px-4 text-slate-700">
                            <div className="font-bold text-slate-900">{party.totalScans.toLocaleString()} Scans</div>
                            <div className="text-[10px] text-slate-500">
                              Inward: {party.inwardScans} | Delivery: {party.deliveryScans}
                            </div>
                          </td>

                          {/* Action Buttons */}
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => {
                                  setSelectedParty(party.partyName);
                                  setMainView("logs");
                                }}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition"
                                title="View scan logs for this client"
                              >
                                <span>Logs</span>
                                <FiArrowRight size={13} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Party Comparison Bar Chart */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-[#122A47]">
                  Client Consumed vs Remaining Available Balance
                </h3>
                <p className="text-xs text-slate-500">
                  Blue = Consumed Quota APIs, Green = Remaining Available Quota APIs
                </p>
              </div>
            </div>

            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={partyComparisonChartData} margin={{ top: 10, right: 20, left: -10, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} interval={0} angle={-10} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      borderRadius: "10px",
                      color: "#fff",
                      fontSize: "12px",
                      border: "none",
                    }}
                    formatter={(val, name, props) => [
                      `${val.toLocaleString()} APIs`,
                      `${name} (${props.payload.fullName})`,
                    ]}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
                  <Bar dataKey="Used API" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Remaining Balance" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* VIEW 2: COMBINED AUDIT SCAN LOGS TABLE                        */}
      {/* ------------------------------------------------------------- */}
      {mainView === "logs" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden space-y-4">
          <div className="p-5 border-b border-slate-100 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-[#122A47]">
                  Audit Scan Logs
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selectedParty !== "all" ? `Filtered for ${selectedParty} — ` : ""}
                  Showing {filteredScans.length.toLocaleString()} verified scan logs
                </p>
              </div>

              {/* Type & Status Filter Buttons */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setSelectedType("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                    selectedType === "all"
                      ? "bg-[#122A47] text-white border-[#122A47]"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  All Types ({scanCounts.all})
                </button>
                <button
                  onClick={() => setSelectedType("Inward")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                    selectedType === "Inward"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-blue-700 border-blue-200 hover:bg-blue-50"
                  }`}
                >
                  Inward ({scanCounts.inward})
                </button>
                <button
                  onClick={() => setSelectedType("Delivery")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                    selectedType === "Delivery"
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white text-purple-700 border-purple-200 hover:bg-purple-50"
                  }`}
                >
                  Delivery ({scanCounts.delivery})
                </button>

                <span className="text-slate-300">|</span>

                <button
                  onClick={() => setSelectedStatus(selectedStatus === "True" ? "all" : "True")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                    selectedStatus === "True"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  }`}
                >
                  ✓ Success ({scanCounts.success})
                </button>
                <button
                  onClick={() => setSelectedStatus(selectedStatus === "False" ? "all" : "False")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                    selectedStatus === "False"
                      ? "bg-rose-600 text-white border-rose-600"
                      : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                  }`}
                >
                  ✕ Failed ({scanCounts.failed})
                </button>
              </div>
            </div>

            {/* Search & Party Filter Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2">
              <div className="relative">
                <FiSearch className="absolute left-3 top-3 text-slate-400" size={15} />
                <input
                  type="text"
                  placeholder="Search Challan, Lot, Party, IP, Message..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                />
              </div>

              <div>
                <select
                  value={selectedParty}
                  onChange={(e) => setSelectedParty(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-[#122A47] bg-white font-semibold text-slate-700"
                >
                  <option value="all">All Verified Parties ({uniqueParties.length})</option>
                  {uniqueParties.map((p, i) => (
                    <option key={i} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              {(searchQuery || selectedParty !== "all" || selectedType !== "all" || selectedStatus !== "all" || datePreset !== "all") && (
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedParty("all");
                      setSelectedType("all");
                      setSelectedStatus("all");
                      setDatePreset("all");
                    }}
                    className="px-3.5 py-2 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition flex items-center gap-1.5"
                  >
                    <FiX size={14} />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Client / Party Name</th>
                  <th className="py-3 px-4">Textile Mill (Party)</th>
                  <th className="py-3 px-4">Challan / Lot No</th>
                  <th className="py-3 px-4">Static IP</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date-Time</th>
                  <th className="py-3 px-4">Bal. API</th>
                  <th className="py-3 px-4">Price</th>
                  <th className="py-3 px-4">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => {
                    const absoluteIndex = (currentPage - 1) * pageSize + index + 1;
                    return (
                      <tr key={row.uniqueKey} className="hover:bg-slate-50/70 transition">
                        <td className="py-3 px-4 text-slate-400 font-bold">{absoluteIndex}</td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider ${
                              row.scanType === "Inward"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {row.scanType}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-900 whitespace-nowrap">
                          {row.partyName}
                        </td>
                        <td className="py-3 px-4 text-slate-700 whitespace-nowrap">
                          {row.PartyName || "--"}
                        </td>
                        <td className="py-3 px-4 font-mono font-bold text-[#122A47] whitespace-nowrap">
                          {row.refNo}
                        </td>
                        <td className="py-3 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                          {row.StaticIP || "--"}
                        </td>
                        <td className="py-3 px-4">
                          {row.isSuccess ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded font-bold text-[11px]">
                              <FiCheckCircle size={12} /> Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 px-2.5 py-1 rounded font-bold text-[11px]">
                              <FiXCircle size={12} /> Failed
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-600 whitespace-nowrap text-[11px] font-medium">
                          {row.Time || "--"}
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-800">
                          {row.BalApiCount !== undefined ? row.BalApiCount : "--"}
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-800">
                          ₹ {row.Price || 0}
                        </td>
                        <td className="py-3 px-4 text-slate-500 max-w-[180px] truncate" title={row.Message || ""}>
                          {row.Message || "--"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="text-center py-10 text-slate-400 font-medium">
                      No scan records match your filter criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-slate-600">
              <span>
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, filteredScans.length)} of {filteredScans.length.toLocaleString()}{" "}
                entries
              </span>
              <span className="text-slate-300">|</span>
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-slate-300 rounded px-2 py-1 text-xs outline-none bg-white font-semibold"
              >
                {[15, 25, 50, 100].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3.5 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition font-bold"
              >
                « Prev
              </button>
              <span className="px-3.5 py-1.5 font-bold text-slate-800">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="px-3.5 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition font-bold"
              >
                Next »
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* VIEW 3: CHARTS & VISUAL ANALYTICS                             */}
      {/* ------------------------------------------------------------- */}
      {mainView === "charts" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-base font-bold text-[#122A47] mb-1">Scan Volume Progression (14 Days)</h3>
            <p className="text-xs text-slate-500 mb-4">Inward vs Delivery scan volume trend over time</p>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timelineChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorInward" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.inward} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={CHART_COLORS.inward} stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorDelivery" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.delivery} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={CHART_COLORS.delivery} stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1e293b", borderRadius: "8px", color: "#fff", fontSize: "12px" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Area
                    type="monotone"
                    dataKey="Inward"
                    stroke={CHART_COLORS.inward}
                    strokeWidth={2}
                    fill="url(#colorInward)"
                  />
                  <Area
                    type="monotone"
                    dataKey="Delivery"
                    stroke={CHART_COLORS.delivery}
                    strokeWidth={2}
                    fill="url(#colorDelivery)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <h3 className="text-base font-bold text-[#122A47] mb-1">Party-wise API Consumption Share</h3>
            <p className="text-xs text-slate-500 mb-4">Percentage breakdown of total API consumption by party</p>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={partyWiseAnalysis.filter((p) => p.usedApi > 0)}
                    dataKey="usedApi"
                    nameKey="partyName"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={3}
                  >
                    {partyWiseAnalysis.map((entry, idx) => (
                      <Cell
                        key={`cell-${idx}`}
                        fill={["#2563eb", "#9333ea", "#10b981", "#f59e0b", "#ec4899", "#06b6d4"][idx % 6]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: "#1e293b", borderRadius: "8px", color: "#fff", fontSize: "12px" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* VIEW 4: VENDOR BILLING, INVOICES & USAGE TRACKING             */}
      {/* ------------------------------------------------------------- */}
      {mainView === "invoices" && (
        <div className="space-y-6">
          {/* Header with Add Payment Button */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <h3 className="text-lg font-bold text-[#122A47]">
                  OCR Vendor Billing, Invoices & Pool Utilization
                </h3>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800 border border-indigo-200">
                  Real-time Consumption Engine
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Track how many credits were purchased from Google Gemini AI / Hub OCR, how many have been consumed across 
                {" "}<span className="font-bold text-slate-700">{vendorUsageAnalytics.totalSystemScans.toLocaleString()} client scans</span>, 
                and remaining reserve balances.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowAddPaymentModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-[#122A47] hover:bg-[#0c2038] active:scale-95 rounded-xl shadow-sm transition"
              >
                <FiPlusCircle size={16} className="text-emerald-400" />
                <span>+ Record New OCR Expense</span>
              </button>
            </div>
          </div>

          {/* 4 KPI Cards for Vendor Quota & Expense Tracking */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Total Purchased Pool */}
            <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
                  Total Purchased Pool
                </span>
                <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl">
                  <FiLayers size={20} />
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-black text-slate-900 tracking-tight">
                  {vendorUsageAnalytics.totalPurchasedCredits.toLocaleString()}
                </span>
                <span className="text-xs font-bold text-indigo-700 uppercase">Credits</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                <span>Vendor Paid (INR):</span>
                <span className="font-bold text-slate-900">₹ {vendorUsageAnalytics.totalVendorExpenseINR.toLocaleString()}</span>
              </div>
              <div className="absolute top-0 left-0 h-1.5 w-full bg-[#122A47]"></div>
            </div>

            {/* Card 2: Consumed from Vendor Pool */}
            <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
                  Pool Consumed by Scans
                </span>
                <div className="p-2.5 bg-blue-50 text-blue-700 rounded-xl">
                  <FiTrendingUp size={20} />
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-black text-blue-700 tracking-tight">
                  {vendorUsageAnalytics.totalConsumedCredits.toLocaleString()}
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  {vendorUsageAnalytics.overallUtilizationPercent}% Used
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                <span>Consumed Expense Value:</span>
                <span className="font-bold text-blue-700">
                  ₹ {(vendorUsageAnalytics.totalConsumedCredits * vendorUsageAnalytics.avgCostPerScan).toFixed(2)}
                </span>
              </div>
              <div className="absolute top-0 left-0 h-1.5 w-full bg-blue-600"></div>
            </div>

            {/* Card 3: Remaining Balance in Pool */}
            <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
                  Remaining Vendor Balance
                </span>
                <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-xl">
                  <FiCheckCircle size={20} />
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-black text-emerald-700 tracking-tight">
                  {vendorUsageAnalytics.remainingVendorCredits.toLocaleString()}
                </span>
                <span className="text-xs font-bold text-emerald-700 uppercase">Credits</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                <span>
                  {vendorUsageAnalytics.deficitCredits > 0 ? (
                    <span className="text-rose-600 font-bold">Unbacked Deficit:</span>
                  ) : (
                    <span>Reserve Value:</span>
                  )}
                </span>
                <span className={`font-bold ${vendorUsageAnalytics.deficitCredits > 0 ? "text-rose-600" : "text-emerald-700"}`}>
                  {vendorUsageAnalytics.deficitCredits > 0
                    ? `${vendorUsageAnalytics.deficitCredits.toLocaleString()} scans`
                    : `₹ ${(vendorUsageAnalytics.remainingVendorCredits * vendorUsageAnalytics.avgCostPerScan).toFixed(2)}`}
                </span>
              </div>
              <div
                className={`absolute top-0 left-0 h-1.5 w-full ${
                  vendorUsageAnalytics.deficitCredits > 0 ? "bg-rose-500" : "bg-emerald-600"
                }`}
              ></div>
            </div>

            {/* Card 4: Profit & Margin Spread */}
            <div className="relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">
                  Gross Profit Margin
                </span>
                <div className="p-2.5 bg-amber-50 text-amber-700 rounded-xl">
                  <FiDollarSign size={20} />
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span
                  className={`text-3xl font-black tracking-tight ${
                    vendorUsageAnalytics.netProfitINR >= 0 ? "text-emerald-700" : "text-rose-700"
                  }`}
                >
                  ₹ {vendorUsageAnalytics.netProfitINR.toLocaleString()}
                </span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  +{vendorUsageAnalytics.profitMarginPercent}%
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                <span>Client Rev. vs Expense:</span>
                <span className="font-bold text-slate-900">
                  ₹ {vendorUsageAnalytics.totalClientRevenue.toLocaleString()} / ₹ {vendorUsageAnalytics.totalVendorExpenseINR.toLocaleString()}
                </span>
              </div>
              <div className="absolute top-0 left-0 h-1.5 w-full bg-amber-500"></div>
            </div>
          </div>

          {/* Visual Pool Utilization Progress Bar */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800 text-sm">Overall Purchased Pool Utilization:</span>
                <span className="px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-800 font-extrabold border border-blue-200">
                  {vendorUsageAnalytics.overallUtilizationPercent}% Consumed
                </span>
              </div>
              <div className="flex items-center gap-4 text-slate-600 font-semibold">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-blue-600 inline-block"></span>
                  Consumed: {vendorUsageAnalytics.totalConsumedCredits.toLocaleString()} Scans
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span>
                  Remaining: {vendorUsageAnalytics.remainingVendorCredits.toLocaleString()} Credits
                </span>
              </div>
            </div>

            <div className="w-full bg-slate-100 rounded-full h-3.5 overflow-hidden flex shadow-inner">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${Math.min(100, vendorUsageAnalytics.overallUtilizationPercent)}%` }}
                title={`${vendorUsageAnalytics.overallUtilizationPercent}% Consumed`}
              ></div>
              <div
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{
                  width: `${Math.max(
                    0,
                    100 - Math.min(100, vendorUsageAnalytics.overallUtilizationPercent)
                  )}%`,
                }}
                title={`${(100 - vendorUsageAnalytics.overallUtilizationPercent).toFixed(1)}% Remaining`}
              ></div>
            </div>

            {vendorUsageAnalytics.deficitCredits > 0 && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FiAlertTriangle size={16} className="text-rose-600 flex-shrink-0" />
                  <span>
                    <strong>Quota Deficit Warning:</strong> Clients have performed{" "}
                    <strong>{vendorUsageAnalytics.totalSystemScans.toLocaleString()} scans</strong>, which exceeds your
                    purchased vendor pool by{" "}
                    <strong>{vendorUsageAnalytics.deficitCredits.toLocaleString()} scans</strong>. Please record the
                    latest vendor bills to reconcile the pool.
                  </span>
                </div>
                <button
                  onClick={() => setShowAddPaymentModal(true)}
                  className="px-3 py-1 bg-rose-600 text-white rounded-lg font-bold hover:bg-rose-700 whitespace-nowrap"
                >
                  + Add Invoice
                </button>
              </div>
            )}
          </div>

          {/* Invoices Breakdown Table with Usage Tracking */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-800 text-sm">
                  Vendor Invoices & FIFO Usage Consumption Breakdown
                </h4>
                <p className="text-[11px] text-slate-500">
                  Each invoice pool is deducted chronologically as scans occur across the system
                </p>
              </div>
              <span className="text-xs text-slate-500 font-semibold">
                {vendorUsageAnalytics.enrichedInvoices.length} Total Bills Recorded
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 uppercase tracking-wider text-[11px]">
                    <th className="py-3.5 px-4">#</th>
                    <th className="py-3.5 px-4">Vendor Name</th>
                    <th className="py-3.5 px-4">Invoice No / Date</th>
                    <th className="py-3.5 px-4">Billing Period</th>
                    <th className="py-3.5 px-4">Purchased Pool</th>
                    <th className="py-3.5 px-4 text-blue-700">Consumed (FIFO)</th>
                    <th className="py-3.5 px-4 text-emerald-700">Remaining Balance</th>
                    <th className="py-3.5 px-4 w-40">Utilization %</th>
                    <th className="py-3.5 px-4">Pool Status</th>
                    <th className="py-3.5 px-4">Rate & Amount</th>
                    <th className="py-3.5 px-4">Invoice File</th>
                    <th className="py-3.5 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ocrPaymentsLoading ? (
                    <tr>
                      <td colSpan={12} className="text-center py-10">
                        <Loader />
                      </td>
                    </tr>
                  ) : vendorUsageAnalytics.enrichedInvoices.length > 0 ? (
                    vendorUsageAnalytics.enrichedInvoices.map((inv, idx) => (
                      <tr key={inv._id || idx} className="hover:bg-slate-50/70 transition">
                        <td className="py-3.5 px-4 text-slate-400 font-bold">{idx + 1}</td>
                        <td className="py-3.5 px-4 font-bold text-slate-900">{inv.VendorName}</td>
                        <td className="py-3.5 px-4">
                          <div className="font-mono font-bold text-[#122A47]">{inv.InvoiceNo || "--"}</div>
                          <div className="text-[10px] text-slate-500">{inv.InvoiceDate || "--"}</div>
                        </td>
                        <td className="py-3.5 px-4 text-slate-600">{inv.BillingPeriod || "--"}</td>

                        {/* Purchased Pool */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-slate-900">
                            {inv.purchasedCredits.toLocaleString()}{" "}
                            <span className="text-[10px] text-slate-400 font-normal">Credits</span>
                          </div>
                        </td>

                        {/* Consumed (FIFO) */}
                        <td className="py-3.5 px-4 bg-blue-50/30">
                          <div className="font-black text-blue-700">
                            {inv.consumedCredits.toLocaleString()}{" "}
                            <span className="text-[10px] font-normal">Scans</span>
                          </div>
                          <div className="text-[10px] text-blue-600 font-semibold">
                            ₹ {inv.consumedValueINR.toLocaleString()}
                          </div>
                        </td>

                        {/* Remaining Credits */}
                        <td className="py-3.5 px-4 bg-emerald-50/30">
                          <div className="font-black text-emerald-700">
                            {inv.remainingCredits.toLocaleString()}{" "}
                            <span className="text-[10px] font-normal">Credits</span>
                          </div>
                          <div className="text-[10px] text-emerald-600 font-semibold">
                            ₹ {inv.remainingValueINR.toLocaleString()}
                          </div>
                        </td>

                        {/* Usage Progress Bar */}
                        <td className="py-3.5 px-4">
                          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
                            <div
                              className={`h-full transition-all ${
                                inv.usagePercent >= 100
                                  ? "bg-slate-500"
                                  : inv.usagePercent > 0
                                  ? "bg-blue-600"
                                  : "bg-slate-200"
                              }`}
                              style={{ width: `${Math.min(100, inv.usagePercent)}%` }}
                            ></div>
                          </div>
                          <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1 font-semibold">
                            <span className="text-blue-700">{inv.usagePercent}% Used</span>
                            <span>{Math.max(0, 100 - inv.usagePercent).toFixed(1)}% Left</span>
                          </div>
                        </td>

                        {/* Pool Status */}
                        <td className="py-3.5 px-4">
                          {inv.poolStatus === "Fully Exhausted" ? (
                            <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-100 border border-slate-300 px-2.5 py-0.5 rounded-full font-bold text-[10px]">
                              Exhausted
                            </span>
                          ) : inv.poolStatus === "Active (Utilizing)" ? (
                            <span className="inline-flex items-center gap-1 text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full font-bold text-[10px]">
                              Active (In Use)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full font-bold text-[10px]">
                              Queued / Unused
                            </span>
                          )}
                        </td>

                        {/* Rate & Financials */}
                        <td className="py-3.5 px-4">
                          <div className="font-extrabold text-slate-900">
                            ₹ {inv.TotalAmountINR?.toLocaleString() || inv.Amount}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            ₹ {inv.unitRate}/scan
                            {inv.Currency !== "INR" && ` (${inv.Currency} ${inv.Amount})`}
                          </div>
                        </td>

                        {/* Attachment */}
                        <td className="py-3.5 px-4">
                          {inv.InvoiceFile && inv.InvoiceFile.FileUrl ? (
                            <a
                              href={`${API_BASE_URL}${inv.InvoiceFile.FileUrl}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                            >
                              <FiEye size={13} />
                              <span>View Bill</span>
                            </a>
                          ) : (
                            <span className="text-slate-400 text-xs">No Bill</span>
                          )}
                        </td>

                        {/* Action */}
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={() => handleDeletePayment(inv._id)}
                            className="text-rose-600 hover:text-rose-800 p-1.5 rounded-lg hover:bg-rose-50 transition"
                            title="Delete this invoice entry"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center py-12 text-slate-400 font-medium">
                        No vendor invoices recorded yet. Click "+ Record New OCR Expense" to add your first vendor bill.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL 1: RECORD OCR VENDOR PAYMENT & INVOICE                  */}
      {/* ------------------------------------------------------------- */}
      {showAddPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-5 bg-[#122A47] text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <FiFileText size={20} className="text-emerald-400" />
                <div>
                  <h3 className="font-bold text-base">Record OCR Vendor Expense & Invoice</h3>
                  <p className="text-[11px] text-slate-300">
                    Add purchased vendor quota to track real-time consumption and gross margins
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAddPaymentModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handlePaymentSubmit} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vendor Name */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Vendor / Provider Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Google Cloud (Gemini API) / Hub OCR"
                    value={paymentForm.VendorName}
                    onChange={(e) => setPaymentForm({ ...paymentForm, VendorName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                  />
                </div>

                {/* Invoice No */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Invoice / Bill Number</label>
                  <input
                    type="text"
                    placeholder="e.g. INV-2026-0901"
                    value={paymentForm.InvoiceNo}
                    onChange={(e) => setPaymentForm({ ...paymentForm, InvoiceNo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                  />
                </div>

                {/* Invoice Date */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={paymentForm.InvoiceDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, InvoiceDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                  />
                </div>

                {/* Billing Period */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Billing Period</label>
                  <input
                    type="text"
                    placeholder="e.g. September 2026"
                    value={paymentForm.BillingPeriod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, BillingPeriod: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Amount Paid *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="e.g. 5000"
                    value={paymentForm.Amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, Amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-bold text-slate-900"
                  />
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Currency</label>
                  <select
                    value={paymentForm.Currency}
                    onChange={(e) => setPaymentForm({ ...paymentForm, Currency: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] bg-white font-medium"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>

                {/* API Credits Purchased */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">API Credits / Scans Purchased *</label>
                  <input
                    type="number"
                    required
                    placeholder="e.g. 50000"
                    value={paymentForm.ApiCreditsPurchased}
                    onChange={(e) => setPaymentForm({ ...paymentForm, ApiCreditsPurchased: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-bold text-indigo-700"
                  />
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Payment Method</label>
                  <select
                    value={paymentForm.PaymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, PaymentMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] bg-white font-medium"
                  >
                    <option value="Bank Transfer">Bank Transfer / NEFT</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="UPI">UPI</option>
                    <option value="PayPal">PayPal</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Dynamic Live Cost Per Scan Calculator Preview */}
              {paymentForm.Amount && parseInt(paymentForm.ApiCreditsPurchased, 10) > 0 && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between text-xs text-emerald-900">
                  <div>
                    <span className="font-bold">Estimated Cost Per Scan:</span>
                    <span className="text-[11px] text-emerald-700 ml-1">
                      (₹{paymentForm.Amount} / {parseInt(paymentForm.ApiCreditsPurchased, 10).toLocaleString()} Credits)
                    </span>
                  </div>
                  <div className="font-extrabold text-sm text-emerald-800">
                    ₹ {((parseFloat(paymentForm.Amount) * (parseFloat(paymentForm.ExchangeRate) || 1)) / parseInt(paymentForm.ApiCreditsPurchased, 10)).toFixed(4)} / scan
                  </div>
                </div>
              )}

              {/* Transaction ID */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Transaction / Reference ID</label>
                <input
                  type="text"
                  placeholder="e.g. TXN-892374928"
                  value={paymentForm.TransactionId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, TransactionId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                />
              </div>

              {/* Upload Invoice File */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Upload Vendor Invoice File (PDF, Image)</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setInvoiceFile(e.target.files[0] || null)}
                  className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-slate-700 font-bold mb-1">Notes / Description</label>
                <textarea
                  rows={2}
                  placeholder="Additional billing details, discount applied, remarks..."
                  value={paymentForm.Notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, Notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#122A47] font-medium"
                ></textarea>
              </div>

              {/* Form Buttons */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowAddPaymentModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addPaymentLoading}
                  className="px-5 py-2 bg-[#122A47] hover:bg-[#0c2038] text-white rounded-xl font-bold transition flex items-center gap-2 disabled:opacity-50"
                >
                  {addPaymentLoading && <FiRefreshCw className="animate-spin" />}
                  <span>Save Invoice & Pool</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODAL 2: API DOCUMENTATION & SCRAMBLE MODAL                    */}
      {/* ------------------------------------------------------------- */}
      {showApiDocModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-5 bg-[#122A47] text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <FiCode size={20} className="text-indigo-400" />
                <h3 className="font-bold text-base">API Documentation & Response Scramble</h3>
              </div>
              <button
                onClick={() => setShowApiDocModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4 text-xs max-h-[75vh] overflow-y-auto">
              {/* Endpoint Details */}
              <div className="space-y-1.5">
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  Unified Endpoint
                </span>
                <div className="flex items-center gap-2 bg-slate-100 p-2.5 rounded-xl font-mono text-slate-800 text-xs border border-slate-200">
                  <span className="px-2 py-0.5 rounded bg-emerald-600 text-white font-bold text-[10px]">GET</span>
                  <span className="truncate">{API_BASE_URL}/ImgToText/ShowCombinedScanLog</span>
                </div>
              </div>

              {/* cURL Snippet */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                    cURL Command
                  </span>
                  <button
                    onClick={() => copyToClipboard(sampleCurl, "curl")}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 hover:text-indigo-900"
                  >
                    {copiedCurl ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
                    <span>{copiedCurl ? "Copied!" : "Copy cURL"}</span>
                  </button>
                </div>
                <pre className="bg-[#1e293b] text-emerald-400 p-3 rounded-xl font-mono text-[11px] overflow-x-auto">
                  {sampleCurl}
                </pre>
              </div>

              {/* Query Parameters */}
              <div className="space-y-1.5">
                <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  Supported Query Parameters
                </span>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200 text-[10px] uppercase">
                      <tr>
                        <th className="py-2 px-3">Parameter</th>
                        <th className="py-2 px-3">Type</th>
                        <th className="py-2 px-3">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                      <tr>
                        <td className="py-2 px-3 font-bold text-indigo-700">datePreset</td>
                        <td className="py-2 px-3 text-slate-500">string</td>
                        <td className="py-2 px-3 text-slate-700 font-sans">all, today, yesterday, 7days, 30days</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-bold text-indigo-700">scanType</td>
                        <td className="py-2 px-3 text-slate-500">string</td>
                        <td className="py-2 px-3 text-slate-700 font-sans">all, Inward, Delivery</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-3 font-bold text-indigo-700">status</td>
                        <td className="py-2 px-3 text-slate-500">string</td>
                        <td className="py-2 px-3 text-slate-700 font-sans">all, True, False</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* JSON Response Scramble */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                    Response JSON Scramble / Schema
                  </span>
                  <button
                    onClick={() => copyToClipboard(sampleScrambleResponse, "scramble")}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 hover:text-indigo-900"
                  >
                    {copiedScramble ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
                    <span>{copiedScramble ? "Copied!" : "Copy Scramble JSON"}</span>
                  </button>
                </div>
                <pre className="bg-[#1e293b] text-slate-200 p-3 rounded-xl font-mono text-[11px] max-h-56 overflow-y-auto">
                  {sampleScrambleResponse}
                </pre>
              </div>

              {/* Exclusion Note */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs">
                <span className="font-bold">Strict Exclusion Active:</span> The API controller strictly filters out
                internal accounts (<code>DYNAMIC SOFTLINK PVT. LTD.</code>, <code>DSLP(OCR)</code>, and{" "}
                <code>Unassigned Client</code>) so only genuine client parties and their quota balances are returned.
              </div>

              {/* Close Button */}
              <div className="pt-3 border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setShowApiDocModal(false)}
                  className="px-5 py-2 bg-[#122A47] hover:bg-[#0c2038] text-white rounded-xl font-bold transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OCRAnalyticsDashboard;
